import { ROUTES } from 'utils/constants/routes';

// Screens where leaving the page loses work in progress, so the browser's unload prompt is armed.
export const CAPTURE_ROUTES = [
    ROUTES.GOVT_ID_V2,
    ROUTES.GOVT_ID_V2_CAPTURE_CAMERA,
    ROUTES.GOVT_ID_V2_CAPTURE_NEXT,
    ROUTES.GOVT_ID_V2_PHOTO_CONFIRM,
    ROUTES.GOVT_ID_V2_CAPTURE_SELFIE,
    ROUTES.GOVT_ID_V2_CAPTURE_SELFIE_LIVENESS,
];

export const CAPTURE_FLOW_MODES = {
    // Both sides in one camera session; the component prompts the user to flip the document.
    AUTO: 'auto',
    // One side per session, with our own between-sides screen in between.
    MANUAL: 'manual',
};

// Keyed by the mode the person is in now; each suggests the other one.
export const MODE_GUIDANCE_COPY = {
    [CAPTURE_FLOW_MODES.AUTO]: {
        TITLE: "Let's try taking the photos yourself",
        BODY: "We couldn't verify your ID on your last few tries. Please turn off Auto Capture and take a photo of each side yourself.",
        TIPS: {
            HOLD_STRAIGHT: 'Hold your phone straight above your ID',
            TAP_SHARP: 'Tap the button when the text looks sharp',
            FRONT_BACK: 'Take the front first, then the back',
        },
        CTA: 'Turn off Auto Capture',
    },
    [CAPTURE_FLOW_MODES.MANUAL]: {
        TITLE: "Let's try Auto Capture",
        BODY: "We couldn't verify your ID on your last few tries. Please turn on Auto Capture, and your photos will be taken for you.",
        TIPS: {
            SURFACE: 'Lay your ID on a plain, dark surface',
            CORNERS: 'Keep all four corners inside the frame',
            HOLD_STILL: 'Hold still until the photo is taken',
        },
        CTA: 'Turn on Auto Capture',
    },
};

export const DIFFERENT_ID_GUIDANCE_COPY = {
    TITLE: "Let's try a different ID",
    BODY: "We couldn't verify this ID after several tries. Please try again with a different government-issued ID.",
    TIPS: {
        CONDITION: "Choose an ID that isn't worn, scratched or damaged",
        PASSPORT: 'Try your passport or another government-issued ID',
        ANOTHER_ID: 'Choose another ID from the accepted documents',
        ORIGINAL: 'Use the original card, not a copy or a photo',
    },
    CTA: 'Try again',
};

// Where the capture engine is, from the camera screen's point of view. A failure sends the user to a
// shared error screen, so nothing renders for it.
export const READER_STATUS = {
    INITIALIZING: 'initializing',
    READY: 'ready',
    FAILED: 'failed',
};

export const DOCUMENT_STATUS = {
    IDLE: 'idle',
    CAPTURING: 'capturing',
    SCREENING: 'screening',
    FINALIZING: 'finalizing',
    SUBMITTING: 'submitting',
    PROCESSING: 'processing',
    READY: 'ready',
    FAILED: 'failed',
};

/**
 * The one non-terminal body the result endpoint emits. Anything else it answers with a 2xx is a
 * verdict, so the poll loops on this value alone rather than on a list of terminal ones.
 */
export const DOCUMENT_RESULT_STATUS = {
    IN_PROGRESS: 'in_progress',
};

export const DOCUMENT_POLL = {
    INITIAL_DELAY_MS: 1000,
    FACTOR: 1.5,
    MAX_DELAY_MS: 5000,
    BUDGET_MS: 120000,
    RESUME_MAX_ATTEMPTS: 10,
};

/**
 * Where the capture screen's resume check is. `CHECKING` is the starting value rather than `DONE`,
 * so the attempts gate is suspended from the first render — before any redirect could fire on a
 * count an in-flight document has already spent.
 */
export const RESUME_PHASE = {
    CHECKING: 'checking',
    RESUMING: 'resuming',
    DONE: 'done',
};

/**
 * The `errorCode` values a processing verdict can carry, copied from the backend's user error-code
 * enum. Every one of them is mapped to a screen in `lib/documentErrors.js`; a code the backend adds
 * later still reaches the generic screen rather than stranding the user.
 */
export const GOVT_ID_ERROR_CODES = {
    RESTRICTED_DOC_TYPE: 'USER_RESTRICTED_DOC_TYPE',
    DL_BACK_IMAGE_REQUIRED: 'USER_DL_BACK_IMAGE_REQUIRED',
    VERIFICATION_NOT_ENABLED: 'USER_GOVT_ID_VERIFICATION_NOT_ENABLED',
    TYPE_NOT_SUPPORTED: 'USER_GOVT_ID_TYPE_NOT_SUPPORTED',
    UNSUPPORTED_DOCUMENT_TYPE: 'USER_GOVT_ID_UNSUPPORTED_DOCUMENT_TYPE',
    ATTEMPTS_EXHAUSTED: 'USER_GOVT_ID_ATTEMPTS_EXHAUSTED',
    IMAGE_NOT_VALID: 'USER_GOVT_ID_IMAGE_NOT_VALID',
    DOCUMENT_NOT_PASSED: 'USER_UPLOADED_DOCUMENT_NOT_PASSED',
    DOCUMENT_EXPIRED: 'USER_GOVT_ID_DOCUMENT_EXPIRED',
    INVALID_DOB: 'USER_GOVT_ID_INVALID_DOB',
    TAMPER_CHECK_FAILED: 'USER_GOVT_ID_TAMPER_CHECK_FAILED',
    VERIFICATION_FAILED: 'USER_GOVT_ID_VERIFICATION_FAILED',
    RESTRICTED_COUNTRY: 'USER_GOVT_ID_RESTRICTED_COUNTRY',
    DOCUMENT_FRONT_NOT_READABLE: 'USER_GOVT_ID_DOCUMENT_FRONT_NOT_READABLE',
    DOCUMENT_BACK_NOT_READABLE: 'USER_GOVT_ID_BACK_IMAGE_NOT_READABLE',
    DOCUMENT_DATA_MISMATCH: 'USER_GOVT_ID_DOCUMENT_DATA_MISMATCH',
    DOCUMENT_LIVENESS_FAILED: 'USER_GOVT_ID_DOCUMENT_LIVENESS_FAILED',
    AUTHENTICITY_CHECK_FAILED: 'USER_GOVT_ID_AUTHENTICITY_CHECK_FAILED',
    CAPTURE_QUALITY_FAILED: 'USER_GOVT_ID_CAPTURE_QUALITY_FAILED',
    INVALID_NAME: 'USER_GOVT_ID_INVALID_NAME',
    DOC_FORMAT_MISMATCH: 'USER_GOVT_ID_DOC_FORMAT_MISMATCH',
};

export const SELFIE_STATUS = {
    IDLE: 'idle',
    READY: 'ready',
    CAPTURING: 'capturing',
    SUBMITTING: 'submitting',
    PROCESSING: 'processing',
    PASSED: 'passed',
    FAILED: 'failed',
};

export const SELFIE_FACE_MATCH_THRESHOLD = 70;

// AWS rejects a request signed more than five minutes from its own clock.
export const MAX_SUPPORTED_CLOCK_SKEW_MS = 5 * 60 * 1000;

export const SELFIE_FAILURE_REASONS = {
    NOT_LIVE: 'NOT_LIVE',
    NO_FACE_MATCH: 'NO_FACE_MATCH',
};

export const LIVENESS_ASSESSMENT = { LIVE: 'Live' };

export const LIVENESS_ERRORS = {
    CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
    TIMEOUT: 'TIMEOUT',
    RUNTIME_ERROR: 'RUNTIME_ERROR',
    FRESHNESS_TIMEOUT: 'FRESHNESS_TIMEOUT',
    SERVER_ERROR: 'SERVER_ERROR',
    FACE_DISTANCE_ERROR: 'FACE_DISTANCE_ERROR',
    MULTIPLE_FACES_ERROR: 'MULTIPLE_FACES_ERROR',
    CAMERA_ACCESS_ERROR: 'CAMERA_ACCESS_ERROR',
    CAMERA_FRAMERATE_ERROR: 'CAMERA_FRAMERATE_ERROR',
    DEFAULT_CAMERA_NOT_FOUND_ERROR: 'DEFAULT_CAMERA_NOT_FOUND_ERROR',
    MOBILE_LANDSCAPE_ERROR: 'MOBILE_LANDSCAPE_ERROR',
};

/**
 * Mirrors of the capture SDK's enums, verified against 9.7.3005. Literals rather than re-exports
 * because the package is only ever imported dynamically, so its enums are not available
 * synchronously.
 */

// EventActions
export const CAPTURE_EVENTS = {
    PROCESS_FINISHED: 'PROCESS_FINISHED',
    CAMERA_PROCESS_STARTED: 'CAMERA_PROCESS_STARTED',
    CAMERA_PROCESS_CLOSED: 'CAMERA_PROCESS_CLOSED',
    CLOSE: 'CLOSE',
    NEW_PAGE_AVAILABLE: 'NEW_PAGE_AVAILABLE',
    NEW_PAGE_STARTED: 'NEW_PAGE_STARTED',
    SERVICE_INITIALIZED: 'SERVICE_INITIALIZED',
    VIDEO_STARTED: 'VIDEO_STARTED',
    PRESS_CAPTURE_BUTTON: 'PRESS_CAPTURE_BUTTON',
    // Carries a different detail shape with no `data.status`, so a handler has to branch on the
    // action before it reads anything else.
    REMOTE_TRANSACTION_UPLOADED: 'REMOTE_TRANSACTION_UPLOADED',
};

// ResponseCode
export const CAPTURE_STATUS = {
    EMPTY: -1,
    ERROR: 0,
    OK: 1,
    TIMEOUT: 2,
};

// ErrorTypes
export const CAPTURE_ERRORS = {
    CAMERA_PERMISSION_DENIED: 'CAMERA_PERMISSION_DENIED',
    NO_CAMERA: 'NO_CAMERA',
    INCORRECT_CAMERA_ID: 'INCORRECT_CAMERA_ID',
    CAMERA_UNKNOWN_ERROR: 'CAMERA_UNKNOWN_ERROR',
    WASM_LICENSE: 'WASM_LICENSE',
    WASM_ERROR: 'WASM_ERROR',
    MISSING_SCENARIO: 'MISSING_SCENARIO',
    INCORRECT_SCENARIO: 'INCORRECT_SCENARIO',
    BAD_CONFIGURATION: 'BAD_CONFIGURATION',
    NOT_SUPPORTED: 'NOT_SUPPORTED',
    INSECURE_PAGE_CONTEXT: 'INSECURE_PAGE_CONTEXT',
    HTTP_NOT_SUPPORTED: 'HTTP_NOT_SUPPORTED',
    CONNECTION_ERROR: 'CONNECTION_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
    FILE_SIZE: 'FILE_SIZE',
    INCORRECT_FILE: 'INCORRECT_FILE',
};

// Set as `reason` on the error a failed reader initialization throws.
export const READER_ERRORS = {
    LICENCE_REJECTED: 'licence_rejected',
};

export const DOCUMENT_SIDES = { FRONT: 'front', BACK: 'back' };

// GraphicFieldType.DOCUMENT_FRONT / DOCUMENT_REAR — the cropped, perspective-corrected sides.
export const DOCUMENT_IMAGE_FIELDS = [
    { fieldType: 207, side: DOCUMENT_SIDES.FRONT },
    { fieldType: 208, side: DOCUMENT_SIDES.BACK },
];

// Result.DOCUMENT_POSITION — the container whose `DocumentPosition.docFormat` is the DocumentFormat
// (ID1, ID3, …) the engine measured the page as. A physical size, not a document type.
export const DOCUMENT_POSITION_RESULT_TYPE = 85;

// DocumentFormat.UNKNOWN
export const DOCUMENT_FORMAT_UNKNOWN = -1;

export const OCR_COMPRESSION_OPTIONS = {
    maxSizeMB: 2,
    useWebWorker: true,
    fileType: 'image/jpeg',
};

/**
 * `rawResponse.ProcessingFinished` — the engine's own verdict on a capture. Only `FINISHED` means the
 * page was accepted, and it is the one thing that says so: a user-pressed photo comes back reported as
 * a success whether the engine took it or not, so this is what a manual capture is judged on.
 */
export const PROCESSING_FINISHED = {
    NOT_FINISHED: 0,
    FINISHED: 1,
    TIMEOUT: 2,
};

// CheckResult — the outcome of one image-quality check.
export const CHECK_RESULT = {
    ERROR: 0,
    OK: 1,
    WAS_NOT_DONE: 2,
};

/**
 * `ImageQualityCheckList` entry types, keyed by the numeric type the response carries.
 *
 * `name` is the `ImageQualityChecks` value the same check goes by in `imageQa.expectedPass`, and it is
 * the reason this table is not just a list of labels: every check reports a result whether or not it
 * is enforced, so a failure only counts once its name is found in that list. Reading it the other way
 * would reject the back of a driving license for having no portrait on it.
 *
 * `failure` is what our own copy is written against. Colorness (3) and handwritten (8) are left out:
 * we never enforce them.
 */
export const QUALITY_CHECKS = {
    0: { name: 'glaresCheck', failure: 'glare' },
    1: { name: 'focusCheck', failure: 'focus' },
    2: { name: 'dpiThreshold', failure: 'resolution' },
    4: { name: 'IQC_Perspective', failure: 'perspective' },
    5: { name: 'documentPosition', failure: 'bounds' },
    7: { name: 'portraitCheck', failure: 'portrait' },
    9: { name: 'brightnessCheck', failure: 'brightness' },
    10: { name: 'occlusionCheck', failure: 'occlusion' },
};

// Why the last photo was not kept, in the user's terms. Keyed by `QUALITY_CHECKS[…].failure`.
export const CAPTURE_HINTS = {
    focus: 'That photo came out blurry. Hold the document flat and steady, then take it again.',
    glare: 'Glare covered part of the document. Tilt it away from the light and take the photo again.',
    resolution: 'That photo was not sharp enough. Move a little closer and take it again.',
    perspective: 'Hold the camera square to the document, then take the photo again.',
    bounds: 'Fit the whole document inside the frame, then take the photo again.',
    occlusion:
        'Something is covering the document. Move it out of the way and take the photo again.',
    brightness: 'The lighting was uneven. Move somewhere brighter and take the photo again.',
    portrait:
        'The photo on the document was not readable. Line it up in the frame and take it again.',
    unreadable:
        'That photo could not be read. Line the document up inside the frame and take it again.',
};

export const DEFAULT_CAPTURE_HINT = CAPTURE_HINTS.unreadable;

export const DOC_FORMAT_MISMATCH_MESSAGE = "We couldn't read your document. Please scan it again.";

/**
 * The break between the two sides in the automatic mode. The body asks for the barcode by name
 * because an unreadable barcode is the most common reason the back fails the check.
 */
export const FLIP_DOCUMENT_COPY = {
    done: 'Front captured',
    title: 'Now flip your ID to the back',
    body: 'Hold it flat and steady inside the frame, away from bright light, so the barcode comes out sharp.',
    resuming: 'Scanning resumes in a moment',
};

// What ran out of time, which is not the same thing in the two modes: one was still looking for the
// document, the other was waiting for the user to take a photo.
export const CAPTURE_TIMEOUT_MESSAGES = {
    [CAPTURE_FLOW_MODES.AUTO]:
        'Scanning took longer than expected. Nothing was submitted, please try again, keeping the document flat and inside the frame.',
    [CAPTURE_FLOW_MODES.MANUAL]:
        'No photo was taken, so nothing was submitted. Try again when you are ready, keeping the document flat and inside the frame.',
};

// An empty list means the workflow accepts every document type.
export const isPassportAccepted = (allowedDocTypes) => {
    const documents = allowedDocTypes ?? [];

    return documents.length === 0 || documents.includes('PASSPORT');
};

/**
 * Title on the capture-instructions screen. One camera session covers the whole document — the
 * component prompts for the flip and ends the session once no side is left — so this sets the
 * expectation of both sides up front instead of naming one. A single-sided document is covered by the
 * passport clause and never gets asked for a second side.
 */
export const getDocumentCaptureTitle = (allowedDocTypes) =>
    isPassportAccepted(allowedDocTypes)
        ? 'Take clear photos of both sides of your ID card or the page of your passport with your photo.'
        : 'Take clear photos of both sides of your ID card.';
